import type { LessonI, ModuleI, PageI } from "../types";

/** Lesson pages that can be toggled as toolbox tools in admin. */
export function isConfigurableToolboxToolPage(
  page: PageI,
): page is PageI & { _id: string } {
  return Boolean(page._id) && !page.isDeleted && !page.archivedAt;
}

/** Checkbox state keyed by page id for the toolbox tools admin page. */
export function topicToolboxToolFlags(topic: ModuleI): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  for (const lesson of topic.lessons ?? []) {
    for (const page of lesson.pages ?? []) {
      if (!isConfigurableToolboxToolPage(page)) continue;
      next[page._id] = Boolean(page.isToolboxTool);
    }
  }
  return next;
}

export type ToolboxToolAdminSectionRow = {
  page: PageI & { _id: string };
  pageTitle: string;
};

/** Topic lessons in order, each with configurable pages only (for admin tools UI). */
export function getToolboxToolAdminSections(topic: ModuleI): {
  lesson: LessonI;
  rows: ToolboxToolAdminSectionRow[];
}[] {
  const out: { lesson: LessonI; rows: ToolboxToolAdminSectionRow[] }[] = [];
  for (const lesson of topic.lessons ?? []) {
    if (!lesson._id) continue;
    const rows: ToolboxToolAdminSectionRow[] = [];
    for (let index = 0; index < lesson.pages.length; index++) {
      const page = lesson.pages[index];
      if (!isConfigurableToolboxToolPage(page)) continue;
      rows.push({
        page,
        pageTitle: page.title?.trim() || `Page ${index + 1}`,
      });
    }
    if (rows.length > 0) out.push({ lesson, rows });
  }
  return out;
}

export function getLessonsWithToolboxToolFlagChanges(
  topic: ModuleI,
  flags: Record<string, boolean>,
  baseline: Record<string, boolean>,
): LessonI[] {
  const toUpdate: LessonI[] = [];
  for (const lesson of topic.lessons ?? []) {
    if (!lesson._id) continue;
    const changed = lesson.pages.some((p) => {
      if (!isConfigurableToolboxToolPage(p)) return false;
      return Boolean(flags[p._id]) !== Boolean(baseline[p._id]);
    });
    if (changed) toUpdate.push(lesson);
  }
  return toUpdate;
}

export function applyToolboxToolFlagsToLesson(
  lesson: LessonI,
  flags: Record<string, boolean>,
): LessonI {
  return {
    ...lesson,
    pages: lesson.pages.map((p) =>
      isConfigurableToolboxToolPage(p)
        ? { ...p, isToolboxTool: Boolean(flags[p._id]) }
        : p,
    ),
  };
}

export function haveToolboxToolFlagsChanged(
  flags: Record<string, boolean>,
  baseline: Record<string, boolean>,
): boolean {
  const keys = new Set([...Object.keys(flags), ...Object.keys(baseline)]);
  for (const id of keys) {
    if (Boolean(flags[id]) !== Boolean(baseline[id])) return true;
  }
  return false;
}

export type ToolboxToolItem = {
  pageId: string;
  lessonId: string;
  lessonTitle: string;
  pageTitle: string;
  pageIndex: number;
};

export type ToolboxToolSectionGroup = {
  sectionId: string;
  sectionTitle: string;
  tools: ToolboxToolItem[];
};

export function getToolboxToolsForTopic(topic: ModuleI): ToolboxToolItem[] {
  const groups = getToolboxToolSectionGroupsForTopic(topic);
  return groups.flatMap((g) => g.tools);
}

export function getToolboxToolSectionGroupsForTopic(
  topic: ModuleI,
): ToolboxToolSectionGroup[] {
  return (topic.lessons ?? [])
    .filter((lesson) => !!lesson._id)
    .flatMap((lesson) => {
      const tools: ToolboxToolItem[] = lesson.pages
        .filter(
          (page) => page.isToolboxTool && isConfigurableToolboxToolPage(page),
        )
        .map((page, index) => ({
          pageId: page._id!,
          lessonId: lesson._id!,
          lessonTitle: lesson.title,
          pageTitle: page.title?.trim() || `Page ${index + 1}`,
          pageIndex: index,
        }));
      if (tools.length === 0) return [];
      return [{ sectionId: lesson._id!, sectionTitle: lesson.title, tools }];
    });
}
